import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { saveActionDetailRequest, getActionDetailRequest } from "../api/actions-api";

import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosNewRounded from "@mui/icons-material/ArrowBackIosNewRounded";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";

import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import FontSize from "@tiptap/extension-font-size";
import type { ActionDetailResponse } from "../dto/actions-dto";
import { useNotification } from "../context/NotificationContext";


type FormValues = {
    id: string;
    title: string;
    when: string;
    place: string;
    date: Dayjs | null;
    description: string;
};

const ActionEdit: React.FC = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { showNotification } = useNotification();

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors, isSubmitting }
    } = useForm<FormValues>({
        defaultValues: {
            id: "new",
            title: "",
            when: "",
            place: "",
            date: null,
            description: ""
        }
    });

    const handleBack = () => {
        if (id === "new")
            navigate("/actions/list/planned");
        else
            navigate(`/actions/${id}`);
    };

    useEffect(() => {
        if (id === "new") return;

        const fetchActionDetail = async () => {
            try {
                const data = await getActionDetailRequest(Number(id));

                const date = data.date ? dayjs(data.date) : null;

                reset({
                    id: data.id?.toString(),
                    title: data.title,
                    when: data.when,
                    place: data.place,
                    date: date,
                    description: data.description
                });
            } catch (err: any) {
                // Global Snackbar handles error
            }
        };

        fetchActionDetail();
    }, [id, reset]);

    const onSubmit = async (data: FormValues) => {
        try {
            const actionDetail: ActionDetailResponse = {
                ...data,
                date: data.date ? data.date.toISOString() : null,
                id: data.id === "new" ? undefined : Number(data.id)
            };

            const response = await saveActionDetailRequest(actionDetail);
            if (!response.success) {
                showNotification(response.message || "Failed to save action.");
                return;
            }

            if (id === "new")
                navigate("/actions/list/planned");
            else
                navigate(`/actions/${id}`);
        } catch (err) {
            // Global Snackbar handles error
        }
    };

    return (
        <>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <IconButton onClick={handleBack}>
                    <ArrowBackIosNewRounded />
                </IconButton>

                <h2 style={{ margin: 0 }}>
                    {t(id === "new" ? "actions.new" : "actions.edit")}
                </h2>
            </Stack>

            <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ maxWidth: 700 }}>
                <Stack spacing={2}>

                    <Controller
                        name="title"
                        control={control}
                        rules={{ required: t("actions.title.required") }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label={t("actions.title")}
                                fullWidth
                                size="small"
                                error={!!errors.title}
                                helperText={errors.title?.message}
                            />
                        )}
                    />

                    <Controller
                        name="when"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label={t("actions.when")}
                                fullWidth
                                size="small"
                                error={!!errors.when}
                                helperText={errors.when?.message}
                            />
                        )}
                    />

                    <Controller
                        name="place"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label={t("actions.place")}
                                fullWidth
                                size="small"
                                error={!!errors.place}
                                helperText={errors.place?.message}
                            />
                        )}
                    />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Controller
                            name="date"
                            control={control}
                            rules={{ required: t("actions.date.required") }}
                            render={({ field }) => (
                                <DatePicker
                                    label={t("actions.date")}
                                    value={field.value}
                                    onChange={field.onChange}
                                    format="DD.MM.YYYY"
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            fullWidth: true,
                                            error: !!errors.date,
                                            helperText: errors.date?.message
                                        }
                                    }}
                                />
                            )}
                        />
                    </LocalizationProvider>

                    {/* Rich Text Editor */}
                    <FormControl fullWidth error={!!errors.description}>
                        <Controller
                            name="description"
                            control={control}
                            render={({ field }) => {
                                const editor = useEditor({
                                    extensions: [
                                        StarterKit,
                                        TextStyle,
                                        FontSize.configure({ types: ["textStyle"] })
                                    ],
                                    content: field.value || "",
                                    onUpdate: ({ editor }) => field.onChange(editor.getHTML())
                                });

                                useEffect(() => {
                                    if (editor && field.value !== editor.getHTML()) {
                                        editor.commands.setContent(field.value || "");
                                    }
                                }, [field.value, editor]);

                                const toggleMark = (mark: string) => {
                                    if (!editor) return;

                                    switch (mark) {
                                        case "bold": editor.chain().focus().toggleBold().run(); break;
                                        case "italic": editor.chain().focus().toggleItalic().run(); break;
                                        case "underline": editor.chain().focus().toggleUnderline().run(); break;
                                        case "strike": editor.chain().focus().toggleStrike().run(); break;
                                        case "bulletList": editor.chain().focus().toggleBulletList().run(); break;
                                        case "orderedList": editor.chain().focus().toggleOrderedList().run(); break;
                                        case "link":
                                            const url = prompt("Enter URL") || "";
                                            if (url)
                                                editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
                                            break;
                                    }
                                };

                                const setFontSize = (size: string) => {
                                    if (!editor) return;
                                    editor.chain().focus().setFontSize(size).run();
                                };

                                return (
                                    <div>

                                        {editor && (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{ mb: 1, flexWrap: "wrap", alignItems: "center" }}
                                            >
                                                {["bold", "italic", "underline", "strike", "bulletList", "orderedList", "link"].map(mark => (
                                                    <Button
                                                        key={mark}
                                                        variant={editor.isActive(mark) ? "contained" : "outlined"}
                                                        size="small"
                                                        onClick={() => toggleMark(mark)}
                                                    >
                                                        {mark === "bold" ? "B" :
                                                            mark === "italic" ? "I" :
                                                                mark === "underline" ? "U" :
                                                                    mark === "strike" ? "S" :
                                                                        mark === "bulletList" ? "• List" :
                                                                            mark === "orderedList" ? "1. List" : "Link"}
                                                    </Button>
                                                ))}

                                                <FormControl size="small">
                                                    <InputLabel>Font Size</InputLabel>
                                                    <Select
                                                        value={editor?.getAttributes("textStyle")?.fontSize || ""}
                                                        label="Font Size"
                                                        onChange={e => setFontSize(e.target.value)}
                                                        sx={{ minWidth: 80 }}
                                                    >
                                                        <MenuItem value="12px">12</MenuItem>
                                                        <MenuItem value="14px">14</MenuItem>
                                                        <MenuItem value="16px">16</MenuItem>
                                                        <MenuItem value="18px">18</MenuItem>
                                                        <MenuItem value="24px">24</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Stack>
                                        )}

                                        <EditorContent
                                            editor={editor}
                                            style={{
                                                minHeight: 150,
                                                border: "1px solid #ccc",
                                                borderRadius: 4,
                                                padding: 8
                                            }}
                                        />

                                    </div>
                                );
                            }}
                        />

                        {errors.description && (
                            <FormHelperText>{errors.description.message}</FormHelperText>
                        )}
                    </FormControl>

                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        sx={{ mt: 1 }}
                    >
                        {isSubmitting ? <CircularProgress size={22} color="inherit" /> : t("button.save")}
                    </Button>

                </Stack>
            </Box>
        </>
    );
};

export default ActionEdit;